import * as React from 'react';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { selectTheme, useAppSelector } from '@libs/store';
import { getThemeFonts } from './themeRender';

export type TSystemColorScheme = 'light' | 'dark';

const SystemColorSchemeContext = createContext<TSystemColorScheme>('light');

export const useSystemColorScheme = (): TSystemColorScheme => useContext(SystemColorSchemeContext);

const getInitialColorScheme = (): TSystemColorScheme =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const CoreProvider = ({ children }: { children: ReactNode }) => {
    const theme = useAppSelector(selectTheme);
    const fontsCss = useMemo(() => getThemeFonts(theme), [theme]);
    const [colorScheme, setColorScheme] = useState<TSystemColorScheme>(getInitialColorScheme);

    useEffect(() => {
        if (window?.matchMedia) {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            const handler = (e: MediaQueryListEvent) => setColorScheme(e.matches ? 'dark' : 'light');
            mq.addEventListener('change', handler);
            return () => mq.removeEventListener('change', handler);
        }
    }, []);

    return (
        <SystemColorSchemeContext.Provider value={colorScheme}>
            {theme?.themeHash && (
                <style href={`core-theme-${theme.themeHash}`} precedence="default">{`
                    @media (prefers-color-scheme: light) {
                        body { background: #FFF; color: #141414; }
                    }
                    @media (prefers-color-scheme: dark) {
                        body { background: #141414; color: #FFF; }
                    }
                    ${fontsCss}
                `}</style>
            )}
            {children}
        </SystemColorSchemeContext.Provider>
    );
};

export { CoreProvider };
